<script setup lang="ts">
import { reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { call } from '../api/http'
import { ledger, type LedgerSchemas, type Rule } from '../api/ledger'
import { useSession } from '../stores/session'
import { useForm } from '../lib/form'
import UiCard from '../components/UiCard.vue'
import UiButton from '../components/UiButton.vue'
import UiField from '../components/UiField.vue'
import ErrorBanner from '../components/ErrorBanner.vue'
import EmptyState from '../components/EmptyState.vue'

type MatchField = LedgerSchemas['RuleMatchField']
const FIELDS: MatchField[] = ['counterparty', 'remittance', 'iban', 'amount']

const { t } = useI18n()
const session = useSession()
const load = useForm()
const create = useForm()
const action = useForm()

const rules = ref<Rule[]>([])
const categories = ref<string[]>([])
const applied = ref<number | null>(null)
const blank = () => ({ match_field: 'counterparty' as MatchField, match_value: '', category: '', priority: 100, is_active: true })
const form = reactive(blank())

watch(
  () => session.workspace?.id,
  () =>
    load.submit(async () => {
      const [r, c] = await Promise.all([call(ledger.GET('/rules')), call(ledger.GET('/categories'))])
      rules.value = r
      categories.value = c.map((x) => x.code)
    }),
  { immediate: true },
)

async function onCreate() {
  const ok = await create.submit(async () => {
    rules.value = [...rules.value, await call(ledger.POST('/rules', { body: { ...form, category: form.category.toUpperCase() } }))]
  })
  if (ok) Object.assign(form, blank())
}

const save = (r: Rule) =>
  action.submit(async () => {
    const { match_field, match_value, category, priority, is_active } = r
    Object.assign(
      r,
      await call(
        ledger.PATCH('/rules/{id}', {
          params: { path: { id: r.id } },
          body: { match_field, match_value, category: category.toUpperCase(), priority, is_active },
        }),
      ),
    )
  })

async function remove(r: Rule) {
  if (!confirm(t('rules.deleteConfirm'))) return
  await action.submit(async () => {
    await call(ledger.DELETE('/rules/{id}', { params: { path: { id: r.id } } }))
    rules.value = rules.value.filter((x) => x.id !== r.id)
  })
}

const apply = () => action.submit(async () => (applied.value = (await call(ledger.POST('/rules/apply'))).updated))
</script>

<template>
  <div class="page">
    <header class="page-header">
      <div>
        <h1>{{ $t('rules.title') }}</h1>
        <p class="muted">{{ $t('rules.subtitle') }}</p>
      </div>
      <UiButton v-if="session.canWrite" variant="outline" data-testid="apply-rules" :loading="action.busy.value" @click="apply">
        {{ $t('rules.apply') }}
      </UiButton>
    </header>

    <p v-if="applied !== null" class="applied" role="status" data-testid="rules-applied">{{ $t('rules.applied', { n: applied }) }}</p>
    <ErrorBanner :error="action.error.value ?? load.error.value" />

    <datalist id="rule-categories">
      <option v-for="c in categories" :key="c" :value="c" />
    </datalist>

    <UiCard v-if="session.canWrite" :title="$t('rules.new')">
      <form class="rule" @submit.prevent="onCreate">
        <UiField :label="$t('rules.field')">
          <select v-model="form.match_field">
            <option v-for="f in FIELDS" :key="f" :value="f">{{ $t(`rules.fields.${f}`) }}</option>
          </select>
        </UiField>
        <UiField :label="$t('rules.value')" :error="create.fieldErr('match_value')">
          <input v-model.trim="form.match_value" required maxlength="200" />
        </UiField>
        <UiField :label="$t('transactions.category')" :error="create.fieldErr('category')">
          <input v-model.trim="form.category" list="rule-categories" required maxlength="32" />
        </UiField>
        <UiField :label="$t('rules.priority')">
          <input v-model.number="form.priority" type="number" min="0" />
        </UiField>
        <UiButton type="submit" :loading="create.busy.value">{{ $t('common.create') }}</UiButton>
      </form>
      <ErrorBanner :error="create.error.value" />
    </UiCard>

    <UiCard>
      <p v-if="load.busy.value && !rules.length" class="muted">{{ $t('common.loading') }}</p>
      <EmptyState v-else-if="!rules.length" :title="$t('rules.empty')" :message="$t('rules.emptyText')" />
      <ul v-else class="list">
        <li v-for="r in rules" :key="r.id">
          <form v-if="session.canWrite" class="rule" @submit.prevent="save(r)">
            <select v-model="r.match_field" :aria-label="$t('rules.field')">
              <option v-for="f in FIELDS" :key="f" :value="f">{{ $t(`rules.fields.${f}`) }}</option>
            </select>
            <input v-model.trim="r.match_value" required maxlength="200" :aria-label="$t('rules.value')" />
            <input v-model.trim="r.category" list="rule-categories" required maxlength="32" :aria-label="$t('transactions.category')" />
            <input v-model.number="r.priority" type="number" min="0" :aria-label="$t('rules.priority')" />
            <div class="row" style="gap: 0.25rem">
              <label class="row small" style="gap: 0.3rem"><input v-model="r.is_active" type="checkbox" class="check" />{{ $t('rules.active') }}</label>
              <UiButton type="submit" size="sm" :disabled="action.busy.value">{{ $t('common.save') }}</UiButton>
              <UiButton size="sm" variant="ghost" :disabled="action.busy.value" @click="remove(r)">{{ $t('common.delete') }}</UiButton>
            </div>
          </form>
          <span v-else :class="{ muted: !r.is_active }">
            {{ $t(`rules.fields.${r.match_field}`) }} “{{ r.match_value }}” → <strong>{{ r.category }}</strong> ({{ r.priority }})
          </span>
        </li>
      </ul>
    </UiCard>
  </div>
</template>

<style scoped>
.rule {
  display: grid;
  grid-template-columns: 9rem 2fr 1fr 5rem auto;
  gap: 0.5rem;
  align-items: end;
  width: 100%;
}
.check {
  width: auto;
  min-height: 0;
}
.applied {
  color: var(--gain);
}
@media (max-width: 760px) {
  .rule {
    grid-template-columns: 1fr 1fr;
  }
}
</style>
